import { createApi } from "@reduxjs/toolkit/query/react"
import { authQuery } from "./authQuery"
import { AddProductDto, ProductDto, ProductsDto, UpdateProductDto } from "@custom-types/products"

export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery: authQuery,
  tagTypes: ["Product", "ProductList"],
  endpoints: (builder) => ({
    getProducts: builder.query<
      ProductsDto,
      { roleHint?: "build" | "whatif" | (string & {}); createMode?: boolean }
    >({
      query: ({ roleHint, createMode } = {}) => {
        if (createMode) return { url: `/products?role_hint=${roleHint}&create_mode=${createMode}` }

        return { url: `/products?role_hint=${roleHint}` }
      },
      providesTags: ["ProductList"],
    }),
    getProductByKey: builder.query<ProductDto, { key: string; orgKey: string; divKey: string }>({
      query: ({ key, orgKey, divKey }) => ({
        url: `/products/${key}--${orgKey}--${divKey}`,
      }),
      providesTags: ["Product"],
    }),
    addProduct: builder.mutation<ProductDto, AddProductDto>({
      query: (body) => ({
        url: "/products",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProductList"],
    }),
    updateProduct: builder.mutation<
      ProductDto,
      {
        key: string
        organisation_key: string
        division_key: string
        body: UpdateProductDto
      }
    >({
      query: ({ key, organisation_key, division_key, body }) => ({
        url: `/products/${key}--${organisation_key}--${division_key}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ProductList", "Product"],
    }),
    deleteProduct: builder.mutation<
      void,
      { key: string; organisation_key: string; division_key: string }
    >({
      query: ({ key, organisation_key, division_key }) => ({
        url: `/products/${key}--${organisation_key}--${division_key}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProductList"],
    }),
    getNodeTags: builder.query<string, { key: string; orgKey: string; divKey: string }>({
      query: ({ key, orgKey, divKey }) => ({
        url: `/products/${key}--${orgKey}--${divKey}/tags`,
        responseHandler: "text",
      }),
      providesTags: ["Product"],
    }),
    putNodeTags: builder.mutation<
      string,
      { key: string; orgKey: string; divKey: string; data: string }
    >({
      query: ({ key, orgKey, divKey, data }) => ({
        url: `/products/${key}--${orgKey}--${divKey}/tags`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Product"],
    }),
    getProductGraphTags: builder.query<string, { key: string; orgKey: string; divKey: string }>({
      query: ({ key, orgKey, divKey }) => ({
        url: `/products/${key}--${orgKey}--${divKey}/graph-tags`,
        responseHandler: "text",
      }),
      providesTags: ["Product"],
    }),
    putProductGraphTags: builder.mutation<
      string,
      { key: string; orgKey: string; divKey: string; data: string }
    >({
      query: ({ key, orgKey, divKey, data }) => ({
        url: `/products/${key}--${orgKey}--${divKey}/graph-tags`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Product"],
    }),
  }),
})

export const {
  useGetProductsQuery,
  useLazyGetProductByKeyQuery,
  useAddProductMutation,
  useDeleteProductMutation,
  useUpdateProductMutation,
  useGetNodeTagsQuery,
  usePutNodeTagsMutation,
  useGetProductGraphTagsQuery,
  usePutProductGraphTagsMutation,
} = productsApi
